import { Trash2, Plus, Shield, Briefcase, Home, Users, ScrollText, Globe, LayoutGrid, ChevronDown, ChevronRight, FolderKanban, BarChart3, Building2 } from "lucide-react";
import { useState } from "react";

// Types
type VDRPermission = {
  roomId: string;
  accessLevel: 'viewer' | 'editor' | 'admin';
};

type DomainGroupPermission = {
  groupId: string;
  accessLevel: 'viewer' | 'editor' | 'admin';
};

type FundraisingPermission = {
  fundraiseId: string;
  accessLevel: 'viewer' | 'editor' | 'admin';
};

type HoldingsCompanyPermission = {
  companyId: string;
  accessLevel: 'viewer' | 'editor' | 'admin';
};

type VDRRoom = {
  id: string;
  name: string;
};

type DomainGroup = {
  id: string;
  name: string;
};

type Fundraise = {
  id: string;
  name: string;
};

type HoldingsCompany = {
  id: string;
  name: string;
};

type PermissionEditorProps = {
  currentPermissions: string[];
  vdrPermissions: VDRPermission[];
  domainGroupPermissions: DomainGroupPermission[];
  fundraisingPermissions: FundraisingPermission[];
  holdingsCompanyPermissions: HoldingsCompanyPermission[];
  allRooms: VDRRoom[];
  allDomainGroups: DomainGroup[];
  allFundraises: Fundraise[];
  allHoldingsCompanies: HoldingsCompany[];
  onChange: (
    permissions: string[],
    vdrPermissions: VDRPermission[],
    domainGroupPermissions: DomainGroupPermission[],
    fundraisingPermissions: FundraisingPermission[],
    holdingsCompanyPermissions: HoldingsCompanyPermission[]
  ) => void;
};

export default function PermissionEditor({
  currentPermissions,
  vdrPermissions,
  domainGroupPermissions,
  fundraisingPermissions = [],
  holdingsCompanyPermissions = [],
  allRooms,
  allDomainGroups,
  allFundraises,
  allHoldingsCompanies = [],
  onChange
}: PermissionEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    admin: true,
    fundraising: true,
    holdings: true,
  });
  
  const has = (key: string) => currentPermissions.includes(key);

  const toggle = (key: string) => {
    let newPerms = [...currentPermissions];
    if (has(key)) {
      newPerms = newPerms.filter(p => p !== key);
      if (key === 'admin') {
        newPerms = newPerms.filter(p => !p.startsWith('admin.'));
      }
      if (key === 'fundraising') {
        newPerms = newPerms.filter(p => !p.startsWith('fundraising.'));
      }
      if (key === 'holdings') {
        newPerms = newPerms.filter(p => !p.startsWith('holdings.'));
      }
    } else {
      newPerms.push(key);
      if (key.startsWith('admin.') && !newPerms.includes('admin')) newPerms.push('admin');
      if (key.startsWith('fundraising.') && !newPerms.includes('fundraising')) newPerms.push('fundraising');
      if (key.startsWith('holdings.') && !newPerms.includes('holdings')) newPerms.push('holdings');
    }
    onChange(newPerms, vdrPermissions, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // VDR Permission Handlers
  const addVdrPerm = () => {
    const available = allRooms.find(r => !vdrPermissions.some(vp => vp.roomId === r.id));
    if (!available) return;
    const newVdrPerms = [...vdrPermissions, { roomId: available.id, accessLevel: 'viewer' as const }];
    onChange(currentPermissions, newVdrPerms, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions);
  };

  const removeVdrPerm = (roomId: string) => {
    const newVdrPerms = vdrPermissions.filter(vp => vp.roomId !== roomId);
    onChange(currentPermissions, newVdrPerms, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions);
  };

  const updateVdrPerm = (roomId: string, updates: Partial<VDRPermission>) => {
    const newVdrPerms = vdrPermissions.map(vp =>
        vp.roomId === roomId ? { ...vp, ...updates } : vp
    );
    onChange(currentPermissions, newVdrPerms, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions);
  };

  // Domain Group Permission Handlers
  const addDgPerm = () => {
    const available = allDomainGroups.find(g => !domainGroupPermissions.some(dgp => dgp.groupId === g.id));
    if (!available) return;
    const newDgPerms = [...domainGroupPermissions, { groupId: available.id, accessLevel: 'viewer' as const }];
    onChange(currentPermissions, vdrPermissions, newDgPerms, fundraisingPermissions, holdingsCompanyPermissions);
  };

  const removeDgPerm = (groupId: string) => {
    const newDgPerms = domainGroupPermissions.filter(dgp => dgp.groupId !== groupId);
    onChange(currentPermissions, vdrPermissions, newDgPerms, fundraisingPermissions, holdingsCompanyPermissions);
  };

  const updateDgPerm = (groupId: string, updates: Partial<DomainGroupPermission>) => {
    const newDgPerms = domainGroupPermissions.map(dgp =>
        dgp.groupId === groupId ? { ...dgp, ...updates } : dgp
    );
    onChange(currentPermissions, vdrPermissions, newDgPerms, fundraisingPermissions, holdingsCompanyPermissions);
  };

  // Fundraising Permission Handlers
  const addFundraisingPerm = () => {
    const available = allFundraises.find(f => !fundraisingPermissions.some(fp => fp.fundraiseId === f.id));
    if (!available) return;
    const newPerms = [...fundraisingPermissions, { fundraiseId: available.id, accessLevel: 'viewer' as const }];
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, newPerms, holdingsCompanyPermissions);
  };

  const removeFundraisingPerm = (fundraiseId: string) => {
    const newPerms = fundraisingPermissions.filter(fp => fp.fundraiseId !== fundraiseId);
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, newPerms, holdingsCompanyPermissions);
  };

  const updateFundraisingPerm = (fundraiseId: string, updates: Partial<FundraisingPermission>) => {
    const newPerms = fundraisingPermissions.map(fp =>
      fp.fundraiseId === fundraiseId ? { ...fp, ...updates } : fp
    );
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, newPerms, holdingsCompanyPermissions);
  };

  // Holdings Company Permission Handlers
  const addHoldingsCompanyPerm = () => {
    const available = allHoldingsCompanies.find(c => !holdingsCompanyPermissions.some(hcp => hcp.companyId === c.id));
    if (!available) return;
    const newPerms = [...holdingsCompanyPermissions, { companyId: available.id, accessLevel: 'viewer' as const }];
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, fundraisingPermissions, newPerms);
  };

  const removeHoldingsCompanyPerm = (companyId: string) => {
    const newPerms = holdingsCompanyPermissions.filter(hcp => hcp.companyId !== companyId);
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, fundraisingPermissions, newPerms);
  };

  const updateHoldingsCompanyPerm = (companyId: string, updates: Partial<HoldingsCompanyPermission>) => {
    const newPerms = holdingsCompanyPermissions.map(hcp =>
      hcp.companyId === companyId ? { ...hcp, ...updates } : hcp
    );
    onChange(currentPermissions, vdrPermissions, domainGroupPermissions, fundraisingPermissions, newPerms);
  };

  return (
    <div className="space-y-1">
      {/* Admin Category */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
        <button 
          type="button"
          onClick={() => toggleSection('admin')}
          className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${has('admin') ? 'bg-indigo-100 text-indigo-600' : 'bg-neutral-200 text-neutral-500'}`}>
              <Shield size={16} />
            </div>
            <div className="text-left">
              <div className="font-medium text-neutral-900">Admin</div>
              <div className="text-xs text-neutral-500">Dashboard, Users & Logs</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input 
              type="checkbox" 
              checked={has('admin')} 
              onChange={(e) => { e.stopPropagation(); toggle('admin'); }}
              className="h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
            />
            {expandedSections.admin ? <ChevronDown size={16} className="text-neutral-400" /> : <ChevronRight size={16} className="text-neutral-400" />}
          </div>
        </button>
        
        {expandedSections.admin && has('admin') && (
          <div className="px-4 py-3 space-y-2 border-t border-neutral-100">
            <PermissionItem 
              icon={Home} 
              label="Home Dashboard" 
              description="View main dashboard and stats"
              checked={has('admin.home')} 
              onChange={() => toggle('admin.home')} 
            />
            <PermissionItem 
              icon={Users} 
              label="Users Management" 
              description="Manage user accounts and permissions"
              checked={has('admin.users')} 
              onChange={() => toggle('admin.users')} 
            />
            <PermissionItem 
              icon={ScrollText} 
              label="Action Logs" 
              description="View system activity logs"
              checked={has('admin.logs')} 
              onChange={() => toggle('admin.logs')} 
            />
          </div>
        )}
      </div>

      {/* Fundraising Category */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
        <button 
          type="button"
          onClick={() => toggleSection('fundraising')}
          className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${has('fundraising') ? 'bg-emerald-100 text-emerald-600' : 'bg-neutral-200 text-neutral-500'}`}>
              <Briefcase size={16} />
            </div>
            <div className="text-left">
              <div className="font-medium text-neutral-900">Fundraising</div>
              <div className="text-xs text-neutral-500">VDR & Domain Groups</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input 
              type="checkbox" 
              checked={has('fundraising')} 
              onChange={(e) => { e.stopPropagation(); toggle('fundraising'); }}
              className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
            />
            {expandedSections.fundraising ? <ChevronDown size={16} className="text-neutral-400" /> : <ChevronRight size={16} className="text-neutral-400" />}
          </div>
        </button>
        
        {expandedSections.fundraising && has('fundraising') && (
          <div className="px-4 py-3 space-y-4 border-t border-neutral-100">
            <PermissionItem 
                icon={FolderKanban} 
                label="Your Raises" 
                description="Access to raises and deal flow"
                checked={has('fundraising.command_center')} 
                onChange={() => toggle('fundraising.command_center')} 
            />

            {has('fundraising.command_center') && (
                <div className="ml-10 mt-2">
                    <GranularAccessSection
                    title="Specific Raise Access"
                    items={fundraisingPermissions}
                    allItems={allFundraises}
                    getItemId={(item) => item.fundraiseId}
                    getOptionId={(opt) => opt.id}
                    getOptionLabel={(opt) => opt.name}
                    onAdd={addFundraisingPerm}
                    onRemove={(id) => removeFundraisingPerm(id)}
                    onUpdateId={(oldId, newId) => updateFundraisingPerm(oldId, { fundraiseId: newId })}
                    onUpdateLevel={(id, level) => updateFundraisingPerm(id, { accessLevel: level as any })}
                    getAccessLevel={(item) => item.accessLevel}
                    emptyMessage="No specific raises assigned. User won't see any raises."
                    />
                </div>
            )}

            <PermissionItem 
                icon={BarChart3} 
                label="Analytics" 
                description="View fundraising analytics"
                checked={has('fundraising.analytics')} 
                onChange={() => toggle('fundraising.analytics')} 
            />

            {/* VDR */}
            <div className="space-y-2">
              <PermissionItem 
                icon={LayoutGrid} 
                label="Virtual Data Rooms" 
                description="Access to VDR section"
                checked={has('fundraising.vdr')} 
                onChange={() => toggle('fundraising.vdr')} 
              />
              
              {has('fundraising.vdr') && (
                <div className="ml-10 mt-2">
                  <GranularAccessSection
                    title="Specific Room Access"
                    items={vdrPermissions}
                    allItems={allRooms}
                    getItemId={(item) => item.roomId}
                    getOptionId={(opt) => opt.id}
                    getOptionLabel={(opt) => opt.name}
                    onAdd={addVdrPerm}
                    onRemove={(id) => removeVdrPerm(id)}
                    onUpdateId={(oldId, newId) => updateVdrPerm(oldId, { roomId: newId })}
                    onUpdateLevel={(id, level) => updateVdrPerm(id, { accessLevel: level as any })}
                    getAccessLevel={(item) => item.accessLevel}
                    emptyMessage="No specific rooms assigned. User won't see any VDRs."
                  />
                </div>
              )}
            </div>

            {/* Domain Groups */}
            <div className="space-y-2">
              <PermissionItem 
                icon={Globe} 
                label="Domain Groups" 
                description="Manage email domain groups"
                checked={has('fundraising.domain_groups')} 
                onChange={() => toggle('fundraising.domain_groups')} 
              />
              
              {has('fundraising.domain_groups') && (
                <div className="ml-10 mt-2">
                  <GranularAccessSection
                    title="Specific Group Access"
                    items={domainGroupPermissions}
                    allItems={allDomainGroups}
                    getItemId={(item) => item.groupId}
                    getOptionId={(opt) => opt.id}
                    getOptionLabel={(opt) => opt.name}
                    onAdd={addDgPerm}
                    onRemove={(id) => removeDgPerm(id)}
                    onUpdateId={(oldId, newId) => updateDgPerm(oldId, { groupId: newId })}
                    onUpdateLevel={(id, level) => updateDgPerm(id, { accessLevel: level as any })}
                    getAccessLevel={(item) => item.accessLevel}
                    emptyMessage="No specific groups assigned. User won't see any domain groups."
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Holdings Category */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
        <button
          type="button"
          onClick={() => toggleSection('holdings')}
          className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${has('holdings') ? 'bg-amber-100 text-amber-600' : 'bg-neutral-200 text-neutral-500'}`}>
              <Building2 size={16} />
            </div>
            <div className="text-left">
              <div className="font-medium text-neutral-900">Holdings</div>
              <div className="text-xs text-neutral-500">Portfolio Companies & Costs</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={has('holdings')}
              onChange={(e) => { e.stopPropagation(); toggle('holdings'); }}
              className="h-4 w-4 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
            />
            {expandedSections.holdings ? <ChevronDown size={16} className="text-neutral-400" /> : <ChevronRight size={16} className="text-neutral-400" />}
          </div>
        </button>

        {expandedSections.holdings && has('holdings') && (
          <div className="px-4 py-3 space-y-4 border-t border-neutral-100">
            {/* Company-Level Access */}
            <div className="space-y-2">
              <div className="ml-10">
                <GranularAccessSection
                  title="Company Access"
                  items={holdingsCompanyPermissions}
                  allItems={allHoldingsCompanies}
                  getItemId={(item) => item.companyId}
                  getOptionId={(opt) => opt.id}
                  getOptionLabel={(opt) => opt.name}
                  onAdd={addHoldingsCompanyPerm}
                  onRemove={(id) => removeHoldingsCompanyPerm(id)}
                  onUpdateId={(oldId, newId) => updateHoldingsCompanyPerm(oldId, { companyId: newId })}
                  onUpdateLevel={(id, level) => updateHoldingsCompanyPerm(id, { accessLevel: level as any })}
                  getAccessLevel={(item) => item.accessLevel}
                  emptyMessage="No specific companies assigned. User won't see any portfolio companies."
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Sub-components
function PermissionItem({ 
  icon: Icon, 
  label, 
  description, 
  checked, 
  onChange 
}: { 
  icon: any; 
  label: string; 
  description: string; 
  checked: boolean; 
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer transition-colors">
      <input 
        type="checkbox" 
        checked={checked} 
        onChange={onChange}
        className="h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
      />
      <div className={`w-6 h-6 rounded flex items-center justify-center ${checked ? 'text-neutral-700' : 'text-neutral-400'}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1">
        <div className={`text-sm font-medium ${checked ? 'text-neutral-900' : 'text-neutral-500'}`}>{label}</div>
        <div className="text-xs text-neutral-400">{description}</div>
      </div>
    </label>
  );
}

function GranularAccessSection<T, O>({
  title,
  items,
  allItems,
  getItemId,
  getOptionId,
  getOptionLabel,
  onAdd,
  onRemove,
  onUpdateId,
  onUpdateLevel,
  getAccessLevel,
  emptyMessage,
}: {
  title: string;
  items: T[];
  allItems: O[];
  getItemId: (item: T) => string;
  getOptionId: (opt: O) => string;
  getOptionLabel: (opt: O) => string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdateId: (oldId: string, newId: string) => void;
  onUpdateLevel: (id: string, level: string) => void;
  getAccessLevel: (item: T) => string;
  emptyMessage: string;
}) {
  const canAddMore = allItems.length > items.length;

  return (
    <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{title}</span>
        {canAddMore && (
          <button 
            type="button"
            onClick={onAdd}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus size={12} />
            Add
          </button>
        )}
      </div>
      
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-neutral-400 italic py-2">{emptyMessage}</div>
        ) : (
          items.map((item) => {
            const itemId = getItemId(item);
            return (
              <div key={itemId} className="flex items-center gap-2 bg-white rounded-md border border-neutral-200 p-2">
                <select 
                  className="flex-1 text-sm border-0 bg-transparent focus:ring-0 p-0 text-neutral-800"
                  value={itemId}
                  onChange={(e) => onUpdateId(itemId, e.target.value)}
                >
                  {allItems.map(opt => (
                    <option 
                      key={getOptionId(opt)} 
                      value={getOptionId(opt)}
                      disabled={items.some(x => getItemId(x) === getOptionId(opt) && getItemId(x) !== itemId)}
                    >
                      {getOptionLabel(opt)}
                    </option>
                  ))}
                </select>
                <select 
                  className="text-xs border border-neutral-200 rounded px-2 py-1 bg-white focus:ring-1 focus:ring-neutral-300"
                  value={getAccessLevel(item)}
                  onChange={(e) => onUpdateLevel(itemId, e.target.value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <button 
                  type="button"
                  onClick={() => onRemove(itemId)}
                  className="p-1 text-neutral-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
